import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import TitleDescription from "../components/TitleDescription";
import { useLocation } from "react-router-dom";
import { ReactComponent as PrismSequenceDiagram } from "../assets/prism-sequence-diagram.svg"
import { ReactComponent as PrismArchitecture } from "../assets/prism-arch.svg"
import Title from "../components/Title";
import Grid from '@mui/material/Grid2';
import TitleDescriptionImage from "../components/TitleDescriptionImage";
import { ReactComponent as CustomizableCategories } from "../assets/customizable-categories.svg";
import { ReactComponent as EntityIdentification } from "../assets/entity-identification.svg";
import { ReactComponent as PatternRecognition } from "../assets/pattern-recognition.svg";
import { fetchAndFilterOpenAPISpec } from "../utils/openapi-filter";


function navigateToSection(hash: string) {
    const element = document.getElementById(hash);
    if (element) {
        var headerOffset = 45;
        var elementPosition = element.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.scrollY - headerOffset;
        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
        });
    }
}

const PrismPage: React.FC = React.memo(() => {
    const rapidocRef = useRef<any>(null);

    const location = useLocation();
    useEffect(() => {
        if (location.hash.length === 0) {
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            })
            return;
        }

        const hash = location.hash.slice(1);
        navigateToSection(hash);
    }, [location.hash]);

    useEffect(() => {
        const loadFilteredSpec = async () => {
            if (rapidocRef.current) {
                try {
                    const filteredSpec = await fetchAndFilterOpenAPISpec({
                        specUrl: 'https://api.prism.finfactor.co.in/v1/prism/openapi',
                        includePaths: [
                            '/v1/prism/auth*',
                            '/v1/prism/tasks*',
                        ]
                    });

                    rapidocRef.current.loadSpec(filteredSpec);
                } catch (error) {
                    console.error('Failed to load API spec:', error);
                }
            }
        };

        loadFilteredSpec();
    }, []);

    return <>

        <Box sx={{
            paddingLeft: 4
        }}>
            <TitleDescription title="Prism" description="Prism is an LLM powered transaction categorization engine that categorizes the unstructured banking narrations into meaningful categories like investment, loan/emi etc to extract actionable insights from user’s spending patterns. Trained  on millions of banking narrations, Prism understands the context of the Indian financial ecosystem." />
        </Box>

        <Box sx={{
            paddingLeft: 4
        }}>
            <>
                <Title title="Why Prism?" />
                <Grid container spacing={3} paddingTop={1}>
                    <Grid size={4}>
                        <TitleDescriptionImage
                            title="Entity identification"
                            description="Prism identifies key entities involved in a transaction, such as merchants, billers, or financial institutions, providing deeper insights into the context of the transactions."
                            svg={EntityIdentification}
                            maxWidth={290}
                        />
                    </Grid>
                    <Grid size={4}>
                        <TitleDescriptionImage
                            title="Pattern recognition"
                            description="Pattern recognition to identify recurring transactions like salary, loan EMI, SIP payments, enhancing categorization accuracy and financial insights."
                            svg={PatternRecognition}
                            maxWidth={290}
                        />
                    </Grid>
                    <Grid size={4}>
                        <TitleDescriptionImage
                            title="Category customization"
                            description="Prism supports category customization to suit diverse financial use cases, including lending and personal finance management."
                            svg={CustomizableCategories}
                            maxWidth={290}
                        />
                    </Grid>
                </Grid>
            </>
        </Box>
        <Box sx={{
            paddingLeft: 4
        }}>
            <Title title="Prism Architecture" paddingBottom={4} paddingTop={6} />
            <PrismArchitecture />
            <>
                <Title title="How Prism Works - Sequence Diagram" paddingBottom={4} paddingTop={6} />
                <PrismSequenceDiagram />
            </>
        </Box>

        <rapi-doc
            ref={rapidocRef}
            allow-try={true}
            allow-authentication={false}
            spec-url={""}
            show-info={false}
            show-header={false}
            render-style="view"
            theme="light"
            primary-color="#6C4FE0"
            bg-color="#FFFFFF"
            text-color="#2C3E50"
            header-color="#1A202C"
            nav-bg-color="#2D3748"
            nav-text-color="#E2E8F0"
            nav-hover-bg-color="#4A5568"
            nav-hover-text-color="#FFFFFF"
            nav-accent-color="#6C4FE0"
            schema-style="table"
            allow-server-selection={false}
            server-url={"https://uat.prism.finfactor.in/"}
            regular-font='"Inter","Roboto","Helvetica","Arial",sans-serif'
            mono-font='"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace'
            font-size="default"
            schema-description-expanded={true}
        ></rapi-doc>
    </>
});

export default PrismPage;
